import dayjs, { Dayjs } from "dayjs"
import { IRegister } from "@/models/meterRegister/meterRegister.types"
import {
  EstimatorMode,
  IEstimateRegisterInput,
  IEstimateRequest,
  IMeterReadEstimate,
  IRegisterEstimate,
  RegisterWrapKind,
} from "@/models/meterReadEstimate/meterReadEstimate.types"
import { formatDate } from "@/helpers/dateHelper"
import { VOLUME_UNIT_SHORT_LABELS, displayRead } from "./estimatorHelper"

export interface IEstimatorResultRow {
  registerLabel: string
  estimate: IRegisterEstimate
  /** The read as the meter would show it, zero-padded to the register. */
  read: string
  /**
   * The projection ran past the top of the register, so the formatted read
   * assumes an ordinary rollover. Still a usable figure, worth pointing out.
   */
  overflowed: boolean
  /**
   * The projection fell below zero. -100 on a five-digit register formats to
   * 99900, a perfectly believable meter value, so this one is never shown as
   * the answer - it says the estimate is suspect, not that the meter rolled.
   */
  wentNegative: boolean
  /** The date the read is estimated for, for the result's own label. */
  targetDateLabel: string
  /**
   * What the read the projection runs from is called on screen, so the
   * breakdown and the result copy use the same words as the field labels.
   * Lower case, for use mid-sentence.
   */
  laterReadLabel: string
  /** Days from that read to the target date, and what falls in them. */
  projectionDays: number | null
  projectionConsumptionKwh: number
  projectionWeight: number
  /** The period between the two reads. TWO_READS only. */
  knownPeriodDays: number | null
  knownPeriodWeight: number | null
  knownPeriodConsumptionKwh: number | null
  /** Register-unit consumption between the reads, for gas. Null on rollover. */
  knownPeriodConsumptionUnits: number | null
  unitLabel: string
  annualConsumptionKwh: number | null
  industryEacAq: number
  differenceKwh: number | null
  differencePercent: number | null
  inputsSummary: string
}

export const registerApiName = (register: IRegister): string =>
  (register.rateName ?? "Standard").toUpperCase()

/*
  Registers are matched by position, not by name. IMeterpoint.registers
  flattens across every meter on the meter point, so an exchanged meter gives
  two unnamed registers that both send registerName STANDARD - matching by name
  collapsed the second onto the first, taking its digit count, its usage and
  its plausibility ceiling with it. The response preserves request order.
*/

const percentDifference = (
  derived: number,
  industry: number,
): number | null => {
  if (!industry) return null

  return ((derived - industry) / industry) * 100
}

/**
 * Every input the estimate actually used, for the breakdown panel (US-06).
 *
 * Only ever states what was sent. It used to end with a digit count, which
 * rendered as "undefined digits" once the form stopped sending registerDigits -
 * a register's digit count is not something we supply any more, so it is not
 * one of the inputs to report.
 */
const summariseInputs = (
  input: IEstimateRegisterInput | undefined,
  laterReadLabel: string,
): string => {
  if (!input) return "-"

  const parts = [`${laterReadLabel} ${input.read2Value} on ${input.read2Date}`]

  // Both halves or neither: a value with no date reads as "on undefined".
  if (input.read1Value !== undefined && input.read1Date !== undefined) {
    parts.unshift(`earlier read ${input.read1Value} on ${input.read1Date}`)
  }
  if (input.eacAq !== undefined) {
    parts.push(`annual consumption ${input.eacAq} kWh`)
  }

  return parts.join(", ")
}

const daysBetween = (from?: string, to?: string): number | null => {
  if (!from || !to) return null

  return dayjs(to).diff(dayjs(from), "day")
}

/**
 * Consumption between the two reads, in register units. Taken from the inputs
 * rather than the response, which does not return it (SO-32399 issue 4). A
 * negative difference means the register rolled over, and reconstructing that
 * here would be reimplementing the backend's rollover handling, so it is left
 * null and only the kWh figure is shown.
 */
const knownPeriodUnits = (input?: IEstimateRegisterInput): number | null => {
  if (input?.read1Value === undefined || input.read1Value === null) return null
  if (input.read2Value < input.read1Value) return null

  return input.read2Value - input.read1Value
}

/**
 * Turns the estimator's response into what the result panel renders, so the
 * mapping is testable without mounting anything.
 */
export const buildResultRows = (
  estimate: IMeterReadEstimate | null,
  request: IEstimateRequest | null,
  registers: IRegister[],
  targetDate: Dayjs,
): IEstimatorResultRow[] => {
  if (!estimate || !request) return []

  /*
    The same rule the field labels use: with two reads the pair is earlier and
    later, with one there is nothing for it to be later than so it is the known
    read. Taken from the API mode, which is TWO_READS for both of the two-read
    UI modes.
  */
  const laterReadLabel =
    request.mode === EstimatorMode.TwoReads ? "later read" : "known read"

  return estimate.registers.map((registerEstimate, index) => {
    const register = registers[index]
    const input = request.registers[index]
    const industryEacAq = register?.usage ?? 0
    const annualConsumptionKwh = registerEstimate.estimatedAqKwh
    const knownWeight = registerEstimate.knownPeriodWeight

    return {
      registerLabel: register?.rateName ?? "Standard",
      estimate: registerEstimate,
      read: displayRead(registerEstimate),
      overflowed: registerEstimate.wrapped === RegisterWrapKind.Overflowed,
      wentNegative: registerEstimate.wrapped === RegisterWrapKind.WentNegative,
      targetDateLabel: formatDate(targetDate),
      projectionDays: daysBetween(input?.read2Date, request.targetDate),
      projectionConsumptionKwh: registerEstimate.estimatedConsumptionKwh,
      projectionWeight: registerEstimate.targetPeriodWeight,
      knownPeriodDays: daysBetween(input?.read1Date, input?.read2Date),
      knownPeriodWeight: knownWeight,
      knownPeriodConsumptionKwh:
        annualConsumptionKwh === null || knownWeight === null
          ? null
          : annualConsumptionKwh * knownWeight,
      knownPeriodConsumptionUnits: knownPeriodUnits(input),
      // Gas only, and from the unit actually sent - electricity is kWh, and
      // volumeUnitFor() would otherwise default it to cubic metres.
      unitLabel: input?.unit ? VOLUME_UNIT_SHORT_LABELS[input.unit] : "",
      annualConsumptionKwh,
      industryEacAq,
      differenceKwh:
        annualConsumptionKwh === null
          ? null
          : annualConsumptionKwh - industryEacAq,
      differencePercent:
        annualConsumptionKwh === null
          ? null
          : percentDifference(annualConsumptionKwh, industryEacAq),
      laterReadLabel,
      inputsSummary: summariseInputs(input, laterReadLabel),
    }
  })
}
